import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CheckCircle2, ChevronLeft, DollarSign, Loader2, X } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { PRACTICE_CASE_TYPES } from "@/constants/practice";

const SettlementNegotiationPage = () => {
  const [form, setForm] = useState({
    case_type: 'personal_injury',
    jurisdiction: 'us_federal',
    case_summary: '',
    client_position: 'plaintiff',
    damages_claimed: '',
    settlement_range_low: '',
    settlement_range_high: '',
    strengths: [],
    weaknesses: [],
    opposing_counsel_style: 'reasonable',
    insurance_involved: false,
    policy_limits: '',
    trial_date: '',
    include_historical_analysis: true
  });
  const [newStrength, setNewStrength] = useState('');
  const [newWeakness, setNewWeakness] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const addStrength = () => { if (newStrength.trim()) { setForm(f => ({...f, strengths: [...f.strengths, newStrength.trim()]})); setNewStrength(''); }};
  const addWeakness = () => { if (newWeakness.trim()) { setForm(f => ({...f, weaknesses: [...f.weaknesses, newWeakness.trim()]})); setNewWeakness(''); }};
  
  const generate = async () => {
    if (!form.case_summary.trim()) { setError('Please provide a case summary'); return; }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...form,
        damages_claimed: form.damages_claimed ? parseFloat(form.damages_claimed) : null,
        settlement_range_low: form.settlement_range_low ? parseFloat(form.settlement_range_low) : null,
        settlement_range_high: form.settlement_range_high ? parseFloat(form.settlement_range_high) : null,
        policy_limits: form.policy_limits ? parseFloat(form.policy_limits) : null
      };
      const res = await axios.post(`${API}/practice/settlement-report`, payload);
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="settlement-negotiation-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center">
              <DollarSign className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Settlement Negotiation</h1>
              <p className="text-muted-foreground">Strategy, talking points, and historical analysis</p>
            </div>
          </div>
        </div>
        
        {error && <Alert variant="destructive" className="mb-6"><AlertTriangle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
        
        {!result ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader><CardTitle>Case Information</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Case Type</Label>
                      <Select value={form.case_type} onValueChange={v => setForm(f => ({...f, case_type: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{PRACTICE_CASE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Client Position</Label>
                      <Select value={form.client_position} onValueChange={v => setForm(f => ({...f, client_position: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="plaintiff">Plaintiff</SelectItem>
                          <SelectItem value="defendant">Defendant</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Case Summary *</Label>
                    <Textarea placeholder="Describe the case, key facts, and current status..." value={form.case_summary} onChange={e => setForm(f => ({...f, case_summary: e.target.value}))} rows={4} />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader><CardTitle>Financial Information</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label>Damages Claimed ($)</Label>
                      <Input type="number" placeholder="500000" value={form.damages_claimed} onChange={e => setForm(f => ({...f, damages_claimed: e.target.value}))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Settlement Range Low ($)</Label>
                      <Input type="number" placeholder="250000" value={form.settlement_range_low} onChange={e => setForm(f => ({...f, settlement_range_low: e.target.value}))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Settlement Range High ($)</Label>
                      <Input type="number" placeholder="400000" value={form.settlement_range_high} onChange={e => setForm(f => ({...f, settlement_range_high: e.target.value}))} />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.insurance_involved} onChange={e => setForm(f => ({...f, insurance_involved: e.target.checked}))} className="rounded" />
                      <span className="text-sm">Insurance Involved</span>
                    </label>
                    {form.insurance_involved && (
                      <div className="flex-1">
                        <Input type="number" placeholder="Policy Limits ($)" value={form.policy_limits} onChange={e => setForm(f => ({...f, policy_limits: e.target.value}))} />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader><CardTitle>Case Strengths & Weaknesses</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-emerald-600">Strengths</Label>
                    <div className="flex gap-2 mt-1">
                      <Input placeholder="Add strength..." value={newStrength} onChange={e => setNewStrength(e.target.value)} onKeyDown={e => e.key === 'Enter' && addStrength()} />
                      <Button onClick={addStrength} variant="outline" size="sm">Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {form.strengths.map((s, i) => <Badge key={i} className="bg-emerald-100 text-emerald-800">{s} <button onClick={() => setForm(f => ({...f, strengths: f.strengths.filter((_, idx) => idx !== i)}))} className="ml-1">×</button></Badge>)}
                    </div>
                  </div>
                  <div>
                    <Label className="text-red-600">Weaknesses</Label>
                    <div className="flex gap-2 mt-1">
                      <Input placeholder="Add weakness..." value={newWeakness} onChange={e => setNewWeakness(e.target.value)} onKeyDown={e => e.key === 'Enter' && addWeakness()} />
                      <Button onClick={addWeakness} variant="outline" size="sm">Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {form.weaknesses.map((w, i) => <Badge key={i} className="bg-red-100 text-red-800">{w} <button onClick={() => setForm(f => ({...f, weaknesses: f.weaknesses.filter((_, idx) => idx !== i)}))} className="ml-1">×</button></Badge>)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="space-y-6">
              <Card className="bg-gradient-to-br from-emerald-600 to-teal-600 text-white border-0">
                <CardContent className="p-6">
                  <Button className="w-full h-14 text-lg gap-2 bg-white text-emerald-700 hover:bg-white/90" onClick={generate} disabled={loading || !form.case_summary.trim()}>
                    {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Analyzing...</> : <><DollarSign className="h-5 w-5" /> Generate Strategy</>}
                  </Button>
                  <label className="flex items-center gap-2 mt-4 cursor-pointer">
                    <input type="checkbox" checked={form.include_historical_analysis} onChange={e => setForm(f => ({...f, include_historical_analysis: e.target.checked}))} className="rounded" />
                    <span className="text-sm text-white/90">Include historical settlements analysis</span>
                  </label>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-0">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold">Settlement Strategy Report</h2>
                <p className="text-white/80">{result.case_type} • {result.client_position}</p>
                <Button variant="outline" onClick={() => setResult(null)} className="mt-4 text-white border-white/20">New Analysis</Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader><CardTitle>Negotiation Strategy & Talking Points</CardTitle></CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">{result.negotiation_strategy}</div>
              </CardContent>
            </Card>
            
            {result.historical_analysis && (
              <Card>
                <CardHeader><CardTitle>Historical Settlements Analysis</CardTitle></CardHeader>
                <CardContent>
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap">{result.historical_analysis}</div>
                </CardContent>
              </Card>
            )}
            
            {result.negotiation_dos_and_donts && (
              <div className="grid gap-4 md:grid-cols-2">
                <Card className="border-emerald-200">
                  <CardHeader className="pb-2"><CardTitle className="text-emerald-600">DO</CardTitle></CardHeader>
                  <CardContent>
                    <ul className="space-y-1">{result.negotiation_dos_and_donts.do?.map((item, i) => <li key={i} className="text-sm flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />{item}</li>)}</ul>
                  </CardContent>
                </Card>
                <Card className="border-red-200">
                  <CardHeader className="pb-2"><CardTitle className="text-red-600">DON'T</CardTitle></CardHeader>
                  <CardContent>
                    <ul className="space-y-1">{result.negotiation_dos_and_donts.dont?.map((item, i) => <li key={i} className="text-sm flex gap-2"><X className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />{item}</li>)}</ul>
                  </CardContent>
                </Card>
              </div>
            )}
            
            <Alert className="border-amber-500 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-xs text-amber-800 whitespace-pre-wrap">{result.disclaimer}</AlertDescription>
            </Alert>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};

// Insurance Report Page

export default SettlementNegotiationPage;
